import React, { useRef, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { throttle } from 'lodash-es';

import { Form } from 'components/Form';
import getAppliedAttributeValues from 'features/sort/getAppliedAttributeValues';
import useVisibleAttributesFilter from 'features/sort/useVisibleAttributesFilter';
import { setDynamicDrawerScrollTop } from 'features/sort/dynamicPersistenceStore';

import { SortFilterAppliedFiltersCollapsible } from '../../SortFilterAppliedFilters';
import { SortFilterAttrGroupCollapsible } from '../../SortFilterAttrGroupViews';
import SortFilterAttributeGroup from '../../SortFilterAttributeGroup';
import SearchBarFilter from '../../SearchBarFilter';
import canShowFilterSearchBar from '../../SearchBarFilter/canShowFilterSearchBar';
import filterDataSortingCallback from '../../../filterDataSortingCallback';
import isMappedTo from '../../../isMappedTo';

const FiltersContent = ({
	appliedFilters,
	attributeGroups,
	canonicalOrderedUrlFragments,
	isNavigating,
	setIsNavigating,
	defaultVisibleMenuCount
}) => {

	const dispatch = useDispatch();
	const drawerScrollTopState = useSelector((state) => state.ui.drawerScrollTop);
	const lastAttributeViewportTopState = useSelector((state) => state.ui.lastAttributeViewportTop);
	const getVisibleAttributes = useVisibleAttributesFilter();
	const defaultValues = getAppliedAttributeValues(attributeGroups);
	const visibleAttributeGroups = attributeGroups
		.sort(filterDataSortingCallback)
		.filter(({ Attributes }) => getVisibleAttributes(Attributes).length > 0);
	const scrollRef = useRef(null);

	const handleScroll = useMemo(() => throttle(() => {

		if (scrollRef.current) {

			dispatch(setDynamicDrawerScrollTop(scrollRef.current.scrollTop));

		}

	}, 150), [ dispatch ]);

	useEffect(() => {

		if (scrollRef.current) {

			if (drawerScrollTopState !== undefined) {

				scrollRef.current.scrollTop = drawerScrollTopState;

			} else if (lastAttributeViewportTopState !== undefined) {

				const { name, y } = lastAttributeViewportTopState;
				const cssSafeName = name.replace(/[^a-zA-Z0-9_-]/g, '');
				const currentAttributeTop = document.querySelector(`.SortFilterAttrGroupCollapsible--${cssSafeName}`)
					?.getBoundingClientRect()?.top ?? 0;
				scrollRef.current.scrollTop = currentAttributeTop - y;

			}

		}

	}, []);

	return (
		<main ref={scrollRef} onScroll={handleScroll}>
			{appliedFilters && appliedFilters.length > 0 && (
				<SortFilterAppliedFiltersCollapsible
					appliedFilters={appliedFilters}
					initiallyOpen
					disclosureIconVariant="plus"
				/>
			)}
			<Form method="POST" defaultValues={defaultValues}>
				{visibleAttributeGroups.map(({
					Name,
					Attributes,
					AttributeGroupFilterDisplayTypeId,
					HeaderImage,
					HeaderImageAltTag,
					BodyImage,
					BodyImageAltTag
				}, index) => {

					const showAttributeGroupBodyImg = isMappedTo.attributeGroup.showBodyImage(
						AttributeGroupFilterDisplayTypeId
					);
					const attributeGroupBodyImage = showAttributeGroupBodyImg ? BodyImage : null;

					const visibleAttributes = getVisibleAttributes(Attributes);

					return (
						<SortFilterAttrGroupCollapsible
							key={Name}
							name={Name}
							attributes={visibleAttributes}
							attributeGroupFilterDisplayTypeId={AttributeGroupFilterDisplayTypeId}
							headerImageSrc={HeaderImage}
							headerImageAltTag={HeaderImageAltTag}
							isNavigating={isNavigating}
							setIsNavigating={setIsNavigating}
							isExpandedInitially={index < defaultVisibleMenuCount}
						>
							<SortFilterAttributeGroup
								attributeGroupFilterDisplayTypeId={AttributeGroupFilterDisplayTypeId}
								attributes={visibleAttributes}
								name={Name}
								bodyImageSrc={attributeGroupBodyImage}
								bodyImageAltTag={BodyImageAltTag}
							/>
						</SortFilterAttrGroupCollapsible>
					);

				})}
				{canShowFilterSearchBar(canonicalOrderedUrlFragments) && (
					<SearchBarFilter
						isLeftNav
						attributeGroups={attributeGroups}
						canonicalOrderedUrlFragments={canonicalOrderedUrlFragments}
					/>
				)}
			</Form>
		</main>
	);

};

export default FiltersContent;
